import type { StripeErrorResponse } from "store/billing/types"
import { type ProductType } from "store/product/types"
import type {
  GlassesDraftListItem,
  GlassesEditorDto,
  GlassesEditorInitialSku,
  GlassesLimits,
  GlassesDigitizationSummary,
} from "types"
import { getPlatformHeaders } from "./platformApiAuth"
import { isBillingPaywallError, syncBillingDenyState } from "./platformBillingState"
import { getPlatformServiceEndpoint } from "./platformServiceEndpoints"
import {
  homeCategoryKeys,
  type AdminHomeResponse,
  type HomeOnboarding,
  type UpdateHomeOnboardingInput,
} from "@/types/home"

export {
  homeCategoryKeys,
  type AdminHomeResponse,
  type HomeCategoryKey,
  type HomeCategorySummary,
  type HomeOnboarding,
  type UpdateHomeOnboardingInput,
} from "@/types/home"
import { GENERIC_USER_ERROR_MESSAGE } from "utils/userFacingErrors"
import type {
  Viewer3DList,
  Viewer3DProduct,
  Viewer3DPublishableKey,
  Viewer3DPublishableKeySecret,
  Viewer3DSavePayload,
  Viewer3DUploadKind,
  Viewer3DUploadUrl,
} from "@/features/viewer-3d/types"
import { z } from "zod"

type AdminProductsResponse = {
  items: unknown[]
  pageCount: number
}

type AdminProductTranslationsResponse = {
  alternateName: Array<{ code: string; name: string }>
  alternateSKUsName: Array<{
    skuId: string
    skuI8N: Array<{ code: string; name: string }>
  }>
}

type AdminProductTypesResponse = {
  items: ProductType[]
}

type AdminUploadUrlResponse = {
  contentType: string
  expiresIn: number
  key: string
  method: string
  uploadUrl: string
}

export type AdminAssetPurpose =
  | "effect_zip"
  | "image"
  | "model_glb"
  | "model_usdz"
  | "swatch"
  | "texture"
  | "thumbnail"

export type AdminPresignedAssetUpload = {
  expiresAt: string
  fields: Record<string, string>
  key: string
  method: "POST"
  url: string
}

export type AdminAssetDto = {
  assetId: string
  contentType?: string
  createdAt: string
  failureReason?: string
  merchantId: string
  originalFileName?: string
  projectId: string
  publicUrl?: string
  purpose: AdminAssetPurpose
  sizeBytes?: number
  status: "deleted" | "failed" | "pending_upload" | "ready" | "uploaded"
  updatedAt: string
  version: number
}

type CreateAssetUploadResponse = {
  asset: AdminAssetDto
  upload: AdminPresignedAssetUpload
}

export type AdminMeMerchant = {
  id: string
  legacyMerchantId?: string
  name: string
  provisioned: boolean
  status: "active" | "suspended"
}

export type AdminMeResponse = {
  identitySource: "admin_authorizer" | "platform_actor_fallback"
  merchant?: AdminMeMerchant
  permissions: string[]
  projects: unknown[]
  role: "editor" | "owner" | "superadmin"
  user: {
    clerkOrgId?: string
    email: string
    name: string
    organizationRole?: string
    provider: string
    userId: string
  }
}

const adminMeNonEmptyString = z.string().trim().min(1)
const adminMeDisplayString = z.string().trim()
const adminMeSchema = z
  .object({
    identitySource: z.enum(["admin_authorizer", "platform_actor_fallback"]),
    merchant: z
      .object({
        id: adminMeNonEmptyString,
        legacyMerchantId: adminMeNonEmptyString.optional(),
        name: adminMeDisplayString,
        provisioned: z.boolean(),
        status: z.enum(["active", "suspended"]),
      })
      .passthrough()
      .optional(),
    permissions: z.array(z.string()),
    projects: z.array(z.unknown()),
    role: z.enum(["editor", "owner", "superadmin"]),
    user: z
      .object({
        clerkOrgId: adminMeNonEmptyString.optional(),
        email: adminMeDisplayString,
        name: adminMeDisplayString,
        organizationRole: adminMeNonEmptyString.optional(),
        provider: adminMeNonEmptyString,
        userId: adminMeNonEmptyString,
      })
      .passthrough(),
  })
  .passthrough()

export const parseAdminMeResponse = (value: unknown): AdminMeResponse =>
  adminMeSchema.parse(value) as AdminMeResponse

const homeOnboardingSchema = z
  .object({
    demoViewedAt: z.string().nullable(),
    derived: z.boolean(),
    publishConfirmedAt: z.string().nullable(),
    selectedCategories: z.array(z.enum(homeCategoryKeys)),
  })
  .passthrough()

const adminHomeSchema = z
  .object({
    categories: z.array(
      z
        .object({
          key: z.enum(homeCategoryKeys),
          productsLive: z.number(),
          productsTotal: z.number(),
        })
        .passthrough()
    ),
    onboarding: homeOnboardingSchema,
  })
  .passthrough()

export const parseAdminHomeResponse = (value: unknown): AdminHomeResponse =>
  adminHomeSchema.parse(value) as AdminHomeResponse

type StartGlassesDigitizationResponse = {
  digitization: GlassesDigitizationSummary
  externalId: string
  productId: string
}

export type IntegrationQrCodeTarget =
  | {
      merchantId: string
      /** Present for platform-native targets: the landing widget authenticates with it. */
      publishableKey?: string
      type: "merchant"
    }
  | {
      activeSkuId?: string
      merchantId: string
      productId: string
      publishableKey?: string
      skuIds: string[]
      type: "product"
    }

export type TryOnIntegration = {
  allowedDomains: string[]
  merchantId: string
  platform: string
  projectId: string
  snippet: { mode: "auto" | "manual" }
  status: "active" | "archived" | "suspended"
}

export type TryOnPublishableKey = {
  createdAt: string
  keyId: string
  keyPrefix: "pk_live" | "pk_test"
  label?: string
  /** The integration's own Store URL origins. */
  allowedOrigins: string[]
  /**
   * The raw key, retrievable because publishable keys are public by design.
   * Null for keys created before the integrations model.
   */
  key: string | null
  scopes: string[]
  status: "active" | "revoked"
  updatedAt: string
}

export type TryOnPublishableKeySecret = TryOnPublishableKey & {
  /** The raw key (creation-response alias of `key`). */
  plaintext: string
}

export type TryOnPreviewLink = {
  expiresAt: string
  previewUrl: string
}

const tryOnPreviewLinkSchema = z.object({
  expiresAt: z.string().datetime(),
  previewUrl: z.string().url(),
})

export type IntegrationQrCodeResponse = {
  downloads: {
    htmlSnippet: string
    png: {
      dataUrl: string
      filename: string
    }
    webp: {
      dataUrl: string
      filename: string
    }
  }
  shortCode: string
  shortLink: string
  target: IntegrationQrCodeTarget
  targetUrl: string
}

export type AdminProductDetailsForQrCode = {
  id?: string | null
  name?: string | null
  skus?: Array<{
    id?: string | null
    name?: string | null
    sku?: string | null
  }> | null
}

const ADMIN_TRANSPORT_FAILURE_COOLDOWN_MS = 30_000

// The admin panel talks to two gateway hosts (admin, viewer3dAdmin). The
// cooldown is per host: one unreachable host must not blank out requests to
// the other for 30 seconds.
type AdminServiceName = "admin" | "viewer3dAdmin"

type AdminTransportFailureState = {
  retryAfter: number
}

const adminTransportFailureStates: Partial<Record<AdminServiceName, AdminTransportFailureState>> =
  {}

const readActiveTransportFailure = (
  service: AdminServiceName
): AdminTransportFailureState | null => {
  const state = adminTransportFailureStates[service]
  if (!state) return null

  if (state.retryAfter <= Date.now()) {
    delete adminTransportFailureStates[service]
    return null
  }

  return state
}

const rememberTransportFailure = (service: AdminServiceName): void => {
  adminTransportFailureStates[service] = {
    retryAfter: Date.now() + ADMIN_TRANSPORT_FAILURE_COOLDOWN_MS,
  }
}

const clearTransportFailure = (service: AdminServiceName): void => {
  delete adminTransportFailureStates[service]
}

export type AdminErrorResponse = StripeErrorResponse

export class AdminRequestError extends Error {
  readonly code: string | null
  readonly data: AdminErrorResponse | null
  readonly status: number

  constructor(message: string, status: number, data: AdminErrorResponse | null) {
    super(message)
    this.name = "AdminRequestError"
    this.code = data?.code ?? null
    this.data = data
    this.status = status
  }
}

class AdminApi {
  private readonly serviceEndpoints: Record<AdminServiceName, Record<"v1" | "v2", string>> = {
    admin: {
      v1: getPlatformServiceEndpoint("admin"),
      v2: getPlatformServiceEndpoint("admin", "v2"),
    },
    viewer3dAdmin: {
      v1: getPlatformServiceEndpoint("viewer3dAdmin"),
      v2: getPlatformServiceEndpoint("viewer3dAdmin", "v2"),
    },
  }

  private async parseAdminError(response: Response): Promise<AdminErrorResponse | null> {
    try {
      return (await response.json()) as AdminErrorResponse
    } catch {
      return null
    }
  }

  private async requestAdminJson<T>(
    path: string,
    options: {
      body?: string
      contentType?: string
      defaultErrorMessage: string
      forceFreshToken?: boolean
      method: "DELETE" | "GET" | "PATCH" | "POST" | "PUT"
      parse?: (value: unknown) => T
      service?: AdminServiceName
      skipTransportFailureCooldown?: boolean
      suppressPaywall?: boolean
      version?: "v1" | "v2"
    }
  ): Promise<T> {
    const service = options.service ?? "admin"

    if (!options.skipTransportFailureCooldown) {
      const activeTransportFailure = readActiveTransportFailure(service)
      if (activeTransportFailure) throw new AdminRequestError(GENERIC_USER_ERROR_MESSAGE, 0, null)
    }

    let response: Response

    try {
      const endpoint = this.serviceEndpoints[service][options.version ?? "v1"]
      response = await fetch(`${endpoint}${path}`, {
        method: options.method,
        headers: await getPlatformHeaders(options.contentType, {
          forceFresh: options.forceFreshToken ?? false,
        }),
        ...(options.body ? { body: options.body } : {}),
      })
    } catch {
      rememberTransportFailure(service)

      throw new AdminRequestError(GENERIC_USER_ERROR_MESSAGE, 0, null)
    }

    clearTransportFailure(service)

    if (!response.ok) {
      const data = await this.parseAdminError(response)

      // The thrown error is user-facing and deliberately generic; the real
      // diagnosis (status + backend code/denyCode) must survive somewhere,
      // or a 403 deny-storm reads as "Oops, something went wrong" with zero
      // signal — the exact shape of the 2026-07 dev incident.
      console.error(`[adminApi] ${options.method} ${path} -> ${response.status}`, data)

      if (!options.suppressPaywall && isBillingPaywallError(data)) syncBillingDenyState(data)

      throw new AdminRequestError(GENERIC_USER_ERROR_MESSAGE, response.status, data)
    }

    const value = (await response.json()) as unknown
    if (!options.parse) return value as T

    try {
      return options.parse(value)
    } catch {
      throw new AdminRequestError(GENERIC_USER_ERROR_MESSAGE, 502, null)
    }
  }

  async listProducts(params: {
    category: string
    limit?: number
    merchantId?: string
    page?: number
    partial?: string
    suppressPaywall?: boolean
  }): Promise<AdminProductsResponse> {
    const query = new URLSearchParams()
    query.set("category", params.category)
    if (params.limit !== undefined) query.set("limit", String(params.limit))
    if (params.merchantId) query.set("merchantId", params.merchantId)
    if (params.page !== undefined) query.set("page", String(params.page))
    if (params.partial) query.set("partial", params.partial)

    return this.requestAdminJson<AdminProductsResponse>(`/products?${query.toString()}`, {
      defaultErrorMessage: "Failed to load products",
      method: "GET",
      suppressPaywall: params.suppressPaywall,
    })
  }

  async listProductTypes(options?: {
    suppressPaywall?: boolean
  }): Promise<AdminProductTypesResponse> {
    return this.requestAdminJson<AdminProductTypesResponse>("/product-types", {
      defaultErrorMessage: "Failed to load product types",
      method: "GET",
      suppressPaywall: options?.suppressPaywall,
    })
  }

  async getProduct(productId: string, options?: { suppressPaywall?: boolean }) {
    return this.requestAdminJson<AdminProductDetailsForQrCode>(
      `/products/${encodeURIComponent(productId)}`,
      {
        defaultErrorMessage: "Failed to load product",
        method: "GET",
        suppressPaywall: options?.suppressPaywall,
      }
    )
  }

  async generateIntegrationQrCode(input: {
    /** Omitted = the caller's own merchant scope (server resolves it from the actor). */
    merchantId?: string
    productId?: string
    skuId?: string
  }): Promise<IntegrationQrCodeResponse> {
    return this.requestAdminJson<IntegrationQrCodeResponse>("/integrations/qr-codes", {
      body: JSON.stringify(input),
      contentType: "application/json",
      defaultErrorMessage: "Failed to generate QR code",
      method: "POST",
    })
  }

  async createTryOnPreviewLink(input: {
    merchantId?: string
    productId: string
    variantId?: string
  }): Promise<TryOnPreviewLink> {
    return this.requestAdminJson<TryOnPreviewLink>("/try-on/preview-links", {
      body: JSON.stringify(input),
      contentType: "application/json",
      defaultErrorMessage: "Failed to create Try-On preview link",
      method: "POST",
      parse: (value) => tryOnPreviewLinkSchema.parse(value),
      version: "v2",
    })
  }

  async saveProduct(
    payload: unknown,
    options?: {
      suppressPaywall?: boolean
    }
  ): Promise<{ id: string }> {
    return this.requestAdminJson<{ id: string }>("/products", {
      body: JSON.stringify(payload),
      contentType: "application/json",
      defaultErrorMessage: "Failed to save product",
      method: "POST",
      suppressPaywall: options?.suppressPaywall,
    })
  }

  // Platform-native "who am I": resolves the caller's merchant (id + status) via
  // the admin authorizer. Used by login to resolve a new Clerk-org merchant
  // without the legacy AppSync resolveMerchantByUName. forceFreshToken +
  // skipTransportFailureCooldown make it safe to poll during provisioning:
  // each attempt uses a new token (new API Gateway authorizer cache key) and is
  // never blocked by a client-side transport cooldown armed mid-poll.
  async getCurrentAdmin(options?: { suppressPaywall?: boolean }): Promise<AdminMeResponse> {
    return this.requestAdminJson<AdminMeResponse>("/me", {
      defaultErrorMessage: "Failed to resolve your account",
      forceFreshToken: true,
      method: "GET",
      parse: parseAdminMeResponse,
      skipTransportFailureCooldown: true,
      suppressPaywall: options?.suppressPaywall,
    })
  }

  // Home drives the default landing page AND the sidebar categories, so it
  // must load for locked/trialing merchants too: the endpoint is
  // merchant_authenticated and the calls never sync the paywall state.
  async getHome(): Promise<AdminHomeResponse> {
    return this.requestAdminJson<AdminHomeResponse>("/home", {
      defaultErrorMessage: "Failed to load the home page",
      method: "GET",
      parse: parseAdminHomeResponse,
      suppressPaywall: true,
      version: "v2",
    })
  }

  async updateHomeOnboarding(
    input: UpdateHomeOnboardingInput
  ): Promise<{ onboarding: HomeOnboarding }> {
    return this.requestAdminJson<{ onboarding: HomeOnboarding }>("/home/onboarding", {
      body: JSON.stringify(input),
      contentType: "application/json",
      defaultErrorMessage: "Failed to save your setup progress",
      method: "PATCH",
      parse: (value) =>
        z.object({ onboarding: homeOnboardingSchema }).passthrough().parse(value) as {
          onboarding: HomeOnboarding
        },
      suppressPaywall: true,
      version: "v2",
    })
  }

  async createAssetUpload(
    payload: {
      contentType: string
      fileName?: string
      merchantId?: string
      purpose: AdminAssetPurpose
      sizeBytes: number
    },
    options?: {
      suppressPaywall?: boolean
    }
  ): Promise<CreateAssetUploadResponse> {
    return this.requestAdminJson<CreateAssetUploadResponse>("/uploads", {
      body: JSON.stringify(payload),
      contentType: "application/json",
      defaultErrorMessage: "Failed to start upload",
      method: "POST",
      suppressPaywall: options?.suppressPaywall,
    })
  }

  async getAsset(
    assetId: string,
    options?: {
      merchantId?: string
      suppressPaywall?: boolean
    }
  ): Promise<AdminAssetDto> {
    const query = new URLSearchParams()
    if (options?.merchantId) query.set("merchantId", options.merchantId)
    const suffix = query.toString() ? `?${query.toString()}` : ""

    return this.requestAdminJson<AdminAssetDto>(`/assets/${encodeURIComponent(assetId)}${suffix}`, {
      defaultErrorMessage: "Failed to load upload",
      method: "GET",
      suppressPaywall: options?.suppressPaywall,
    })
  }

  async toggleProductVisibility(
    productId: string,
    visible: boolean,
    options?: {
      suppressPaywall?: boolean
    }
  ): Promise<{ success: boolean }> {
    return this.requestAdminJson<{ success: boolean }>(
      `/products/${encodeURIComponent(productId)}/visibility`,
      {
        body: JSON.stringify({ visible }),
        contentType: "application/json",
        defaultErrorMessage: "Failed to update product visibility",
        method: "PATCH",
        suppressPaywall: options?.suppressPaywall,
      }
    )
  }

  async deleteProduct(productId: string, options?: { suppressPaywall?: boolean }) {
    return this.requestAdminJson<{ success: boolean }>(
      `/products/${encodeURIComponent(productId)}`,
      {
        defaultErrorMessage: "Failed to delete product",
        method: "DELETE",
        suppressPaywall: options?.suppressPaywall,
      }
    )
  }

  async getGlassesEditor(
    productId: string,
    options?: {
      suppressPaywall?: boolean
    }
  ): Promise<GlassesEditorDto> {
    return this.requestAdminJson<GlassesEditorDto>(
      `/glasses/products/${encodeURIComponent(productId)}`,
      {
        defaultErrorMessage: "Couldn't open this item",
        method: "GET",
        suppressPaywall: options?.suppressPaywall,
      }
    )
  }

  async createGlassesDraft(
    payload: {
      cover: string
      externalId?: string | null
      initialSkus?: GlassesEditorInitialSku[]
      name: string
      url?: string | null
    },
    options?: {
      suppressPaywall?: boolean
    }
  ): Promise<GlassesEditorDto> {
    return this.requestAdminJson<GlassesEditorDto>("/glasses/drafts", {
      body: JSON.stringify(payload),
      contentType: "application/json",
      defaultErrorMessage: "Couldn't save",
      method: "POST",
      suppressPaywall: options?.suppressPaywall,
    })
  }

  async getGlassesDraft(
    draftId: string,
    options?: {
      suppressPaywall?: boolean
    }
  ): Promise<GlassesEditorDto> {
    return this.requestAdminJson<GlassesEditorDto>(
      `/glasses/drafts/${encodeURIComponent(draftId)}`,
      {
        defaultErrorMessage: "Couldn't open this item",
        method: "GET",
        suppressPaywall: options?.suppressPaywall,
      }
    )
  }

  async listGlassesDrafts(
    params: {
      merchantId: string
      partial?: string
    },
    options?: {
      suppressPaywall?: boolean
    }
  ): Promise<GlassesDraftListItem[]> {
    const query = new URLSearchParams()
    query.set("merchantId", params.merchantId)
    if (params.partial?.trim()) query.set("partial", params.partial.trim())

    return this.requestAdminJson<GlassesDraftListItem[]>(`/glasses/drafts?${query.toString()}`, {
      defaultErrorMessage: "Couldn't load drafts",
      method: "GET",
      suppressPaywall: options?.suppressPaywall,
    })
  }

  async getGlassesLimits(
    params: {
      merchantId: string
    },
    options?: {
      suppressPaywall?: boolean
    }
  ): Promise<GlassesLimits> {
    const query = new URLSearchParams()
    query.set("merchantId", params.merchantId)

    return this.requestAdminJson<GlassesLimits>(`/glasses/limits?${query.toString()}`, {
      defaultErrorMessage: "Couldn't load limits",
      method: "GET",
      suppressPaywall: options?.suppressPaywall,
    })
  }

  async saveGlassesDraft(
    draftId: string,
    payload: {
      cover: string
      externalId?: string | null
      initialSkus?: GlassesEditorInitialSku[]
      name: string
      url?: string | null
    },
    options?: {
      suppressPaywall?: boolean
    }
  ): Promise<GlassesEditorDto> {
    return this.requestAdminJson<GlassesEditorDto>(
      `/glasses/drafts/${encodeURIComponent(draftId)}`,
      {
        body: JSON.stringify(payload),
        contentType: "application/json",
        defaultErrorMessage: "Couldn't save",
        method: "PUT",
        suppressPaywall: options?.suppressPaywall,
      }
    )
  }

  async deleteGlassesDraft(
    draftId: string,
    options?: {
      suppressPaywall?: boolean
    }
  ): Promise<{ success: true }> {
    return this.requestAdminJson<{ success: true }>(
      `/glasses/drafts/${encodeURIComponent(draftId)}`,
      {
        defaultErrorMessage: "Couldn't cancel draft",
        method: "DELETE",
        suppressPaywall: options?.suppressPaywall,
      }
    )
  }

  async finalizeGlassesDraft(
    draftId: string,
    payload: {
      cover: string
      effectUrl?: string | null
      externalId?: string | null
      initialSkus?: GlassesEditorInitialSku[]
      name: string
      url?: string | null
      visible: boolean
    },
    options?: {
      suppressPaywall?: boolean
    }
  ): Promise<GlassesEditorDto> {
    return this.requestAdminJson<GlassesEditorDto>(
      `/glasses/drafts/${encodeURIComponent(draftId)}/finalize`,
      {
        body: JSON.stringify(payload),
        contentType: "application/json",
        defaultErrorMessage: "Couldn't save",
        method: "POST",
        suppressPaywall: options?.suppressPaywall,
      }
    )
  }

  async saveGlassesEditor(
    productId: string,
    payload: {
      cover: string
      effectUrl?: string | null
      externalId?: string | null
      name: string
      url?: string | null
      visible: boolean
    },
    options?: {
      suppressPaywall?: boolean
    }
  ): Promise<GlassesEditorDto> {
    return this.requestAdminJson<GlassesEditorDto>(
      `/glasses/products/${encodeURIComponent(productId)}`,
      {
        body: JSON.stringify(payload),
        contentType: "application/json",
        defaultErrorMessage: "Couldn't save",
        method: "PUT",
        suppressPaywall: options?.suppressPaywall,
      }
    )
  }

  async createGlassesSourceImageUploadUrl(
    payload: {
      contentType: string
    },
    options?: {
      suppressPaywall?: boolean
    }
  ): Promise<AdminUploadUrlResponse> {
    return this.requestAdminJson<AdminUploadUrlResponse>("/glasses/source-images/upload-url", {
      body: JSON.stringify(payload),
      contentType: "application/json",
      defaultErrorMessage: "Couldn't upload photo",
      method: "POST",
      suppressPaywall: options?.suppressPaywall,
    })
  }

  async startGlassesDraftDigitization(
    draftId: string,
    payload: {
      cover: string
      externalId?: string | null
      initialSkus?: GlassesEditorInitialSku[]
      name: string
      sourceImageKeys: string[]
      url?: string | null
    },
    options?: {
      suppressPaywall?: boolean
    }
  ): Promise<GlassesEditorDto> {
    return this.requestAdminJson<GlassesEditorDto>(
      `/glasses/drafts/${encodeURIComponent(draftId)}/digitization-jobs`,
      {
        body: JSON.stringify(payload),
        contentType: "application/json",
        defaultErrorMessage: "Couldn't start",
        method: "POST",
        suppressPaywall: options?.suppressPaywall,
      }
    )
  }

  async startInitialGlassesDraftDigitization(
    payload: {
      cover: string
      externalId?: string | null
      initialSkus?: GlassesEditorInitialSku[]
      name: string
      sourceImageKeys: string[]
      url?: string | null
    },
    options?: {
      suppressPaywall?: boolean
    }
  ): Promise<GlassesEditorDto> {
    return this.requestAdminJson<GlassesEditorDto>("/glasses/draft-digitization-jobs", {
      body: JSON.stringify(payload),
      contentType: "application/json",
      defaultErrorMessage: "Couldn't start",
      method: "POST",
      suppressPaywall: options?.suppressPaywall,
    })
  }

  async startGlassesDigitization(
    payload: {
      cover: string
      externalId?: string | null
      initialSkus?: GlassesEditorInitialSku[]
      name: string
      productId?: string | null
      sourceImageKeys: string[]
      url?: string | null
    },
    options?: {
      suppressPaywall?: boolean
    }
  ): Promise<StartGlassesDigitizationResponse> {
    return this.requestAdminJson<StartGlassesDigitizationResponse>("/glasses/digitization-jobs", {
      body: JSON.stringify(payload),
      contentType: "application/json",
      defaultErrorMessage: "Couldn't start",
      method: "POST",
      suppressPaywall: options?.suppressPaywall,
    })
  }

  async getUiCustomization(
    merchantId: string,
    options?: {
      suppressPaywall?: boolean
    }
  ) {
    const query = new URLSearchParams({ merchantId })
    return this.requestAdminJson<{ styles: string }>(`/ui-customization?${query.toString()}`, {
      defaultErrorMessage: "Failed to load UI customization",
      method: "GET",
      suppressPaywall: options?.suppressPaywall,
    })
  }

  async saveUiCustomization(
    payload: {
      data: string
      mainLogoAssetId?: string
      merchantId: string
      removeMainLogo?: boolean
      removeShortLogo?: boolean
      shortLogoAssetId?: string
    },
    options?: {
      suppressPaywall?: boolean
    }
  ) {
    return this.requestAdminJson<{ success: boolean }>("/ui-customization", {
      body: JSON.stringify(payload),
      contentType: "application/json",
      defaultErrorMessage: "Failed to save UI customization",
      method: "PUT",
      suppressPaywall: options?.suppressPaywall,
    })
  }

  async getProductTranslations(
    productId: string,
    skuIds: string[],
    options?: {
      suppressPaywall?: boolean
    }
  ): Promise<AdminProductTranslationsResponse> {
    const query = new URLSearchParams({
      skuIds: skuIds.join(","),
    })

    return this.requestAdminJson<AdminProductTranslationsResponse>(
      `/products/${encodeURIComponent(productId)}/translations?${query.toString()}`,
      {
        defaultErrorMessage: "Failed to load product translations",
        method: "GET",
        suppressPaywall: options?.suppressPaywall,
      }
    )
  }

  async saveProductTranslations(
    productId: string,
    payload: {
      data: string
      merchantId: string
    },
    options?: {
      suppressPaywall?: boolean
    }
  ) {
    return this.requestAdminJson<{ success: boolean }>(
      `/products/${encodeURIComponent(productId)}/translations`,
      {
        body: JSON.stringify(payload),
        contentType: "application/json",
        defaultErrorMessage: "Failed to save product translations",
        method: "POST",
        suppressPaywall: options?.suppressPaywall,
      }
    )
  }

  async saveSkuTranslations(
    skuId: string,
    payload: {
      data: string
      merchantId: string
      productId: string
    },
    options?: {
      suppressPaywall?: boolean
    }
  ) {
    return this.requestAdminJson<{ success: boolean }>(
      `/skus/${encodeURIComponent(skuId)}/translations`,
      {
        body: JSON.stringify(payload),
        contentType: "application/json",
        defaultErrorMessage: "Failed to save SKU translations",
        method: "POST",
        suppressPaywall: options?.suppressPaywall,
      }
    )
  }

  async listViewer3DProducts(params?: { cursor?: string; limit?: number }): Promise<Viewer3DList> {
    const query = new URLSearchParams()
    if (params?.limit !== undefined) query.set("limit", String(params.limit))
    if (params?.cursor) query.set("cursor", params.cursor)
    const suffix = query.toString() ? `?${query.toString()}` : ""

    return this.requestAdminJson<Viewer3DList>(`/3d-products${suffix}`, {
      defaultErrorMessage: "Failed to load 3D products",
      method: "GET",
    })
  }

  async getViewer3DProduct(productId: string): Promise<Viewer3DProduct> {
    return this.requestAdminJson<Viewer3DProduct>(`/3d-products/${encodeURIComponent(productId)}`, {
      defaultErrorMessage: "Failed to load 3D product",
      method: "GET",
    })
  }

  async createViewer3DProduct(payload: {
    category: string
    externalId?: string | null
    name: string
    productPageUrl?: string | null
  }): Promise<Viewer3DProduct> {
    return this.requestAdminJson<Viewer3DProduct>("/3d-products", {
      body: JSON.stringify(payload),
      contentType: "application/json",
      defaultErrorMessage: "Failed to create 3D product",
      method: "POST",
    })
  }

  async updateViewer3DProduct(
    productId: string,
    payload: Viewer3DSavePayload
  ): Promise<Viewer3DProduct> {
    return this.requestAdminJson<Viewer3DProduct>(`/3d-products/${encodeURIComponent(productId)}`, {
      body: JSON.stringify(payload),
      contentType: "application/json",
      defaultErrorMessage: "Failed to save 3D product",
      method: "PUT",
    })
  }

  async deleteViewer3DProduct(productId: string): Promise<{ success: boolean }> {
    return this.requestAdminJson<{ success: boolean }>(
      `/3d-products/${encodeURIComponent(productId)}`,
      {
        defaultErrorMessage: "Failed to delete 3D product",
        method: "DELETE",
      }
    )
  }

  async createViewer3DUploadUrl(
    productId: string,
    payload: { kind: Viewer3DUploadKind; variantId?: string }
  ): Promise<Viewer3DUploadUrl> {
    return this.requestAdminJson<Viewer3DUploadUrl>(
      `/3d-products/${encodeURIComponent(productId)}/upload-url`,
      {
        body: JSON.stringify(payload),
        contentType: "application/json",
        defaultErrorMessage: "Failed to start upload",
        method: "POST",
      }
    )
  }

  async createViewer3DDraftUrl(productId: string, variantId: string): Promise<{ url: string }> {
    return this.requestAdminJson<{ url: string }>(
      `/3d-products/${encodeURIComponent(productId)}/draft-url`,
      {
        body: JSON.stringify({ variantId }),
        contentType: "application/json",
        defaultErrorMessage: "Failed to load model preview",
        method: "POST",
      }
    )
  }

  async publishViewer3DProduct(productId: string): Promise<Viewer3DProduct> {
    return this.requestAdminJson<Viewer3DProduct>(
      `/3d-products/${encodeURIComponent(productId)}/publish`,
      {
        defaultErrorMessage: "Failed to publish",
        method: "POST",
      }
    )
  }

  async unpublishViewer3DProduct(productId: string): Promise<Viewer3DProduct> {
    return this.requestAdminJson<Viewer3DProduct>(
      `/3d-products/${encodeURIComponent(productId)}/unpublish`,
      {
        defaultErrorMessage: "Failed to unpublish",
        method: "POST",
      }
    )
  }

  async listViewer3DPublishableKeys(
    merchantId?: string
  ): Promise<{ items: Viewer3DPublishableKey[] }> {
    return this.requestAdminJson<{ items: Viewer3DPublishableKey[] }>(
      this.withMerchantQuery("/3d-publishable-keys", merchantId),
      {
        defaultErrorMessage: "Failed to load keys",
        method: "GET",
      }
    )
  }

  async createViewer3DPublishableKey(payload: {
    allowedOrigins: string[]
    label?: string | null
    merchantId?: string
  }): Promise<Viewer3DPublishableKeySecret> {
    return this.requestAdminJson<Viewer3DPublishableKeySecret>("/3d-publishable-keys", {
      body: JSON.stringify(payload),
      contentType: "application/json",
      defaultErrorMessage: "Failed to create key",
      method: "POST",
    })
  }

  async revokeViewer3DPublishableKey(
    keyHash: string,
    merchantId?: string
  ): Promise<{ success: boolean }> {
    return this.requestAdminJson<{ success: boolean }>(
      this.withMerchantQuery(
        `/3d-publishable-keys/${encodeURIComponent(keyHash)}/revoke`,
        merchantId
      ),
      {
        defaultErrorMessage: "Failed to revoke key",
        method: "POST",
      }
    )
  }

  /**
   * Returns the merchant's reusable 3D demo token, minting it on first call.
   * Idempotent: every "View demo" button calls this and gets the same token,
   * allowlisted for every platform stand.
   *
   * v2-only route with no admin-host sibling: since the admin decomposition
   * (release A) it is served exclusively by the viewer3dAdmin gateway.
   */
  async ensureViewer3DDemoKey(): Promise<{ allowedOrigins: string[]; token: string }> {
    return this.requestAdminJson<{ allowedOrigins: string[]; token: string }>(
      "/3d-publishable-keys/demo",
      {
        defaultErrorMessage: "Failed to prepare the demo link",
        method: "POST",
        service: "viewer3dAdmin",
        version: "v2",
      }
    )
  }

  /** Rename an integration or change its Store URL — the key never changes. */
  async updateViewer3DPublishableKeyOrigins(
    keyHash: string,
    payload: { allowedOrigins?: string[]; label?: string; merchantId?: string }
  ): Promise<Viewer3DPublishableKey> {
    return this.requestAdminJson<Viewer3DPublishableKey>(
      `/3d-publishable-keys/${encodeURIComponent(keyHash)}/origins`,
      {
        body: JSON.stringify(payload),
        contentType: "application/json",
        defaultErrorMessage: "Failed to update the integration",
        method: "PUT",
      }
    )
  }

  // Try-On widget integration (platform-native, publishable-key based).
  // Allowed domains live on the merchant's default PROJECT (unlike 3D viewer
  // keys, which carry per-key origins) — the server reconciles SDK credentials
  // for every domain before a key can be minted.

  private withMerchantQuery(path: string, merchantId?: string): string {
    return merchantId ? `${path}?merchantId=${encodeURIComponent(merchantId)}` : path
  }

  async getTryOnIntegration(merchantId?: string): Promise<TryOnIntegration> {
    return this.requestAdminJson<TryOnIntegration>(
      this.withMerchantQuery("/try-on/integration", merchantId),
      {
        defaultErrorMessage: "Failed to load Try-On integration",
        method: "GET",
      }
    )
  }

  async updateTryOnIntegration(payload: {
    allowedDomains: string[]
    merchantId?: string
  }): Promise<TryOnIntegration> {
    return this.requestAdminJson<TryOnIntegration>("/try-on/integration", {
      body: JSON.stringify(payload),
      contentType: "application/json",
      defaultErrorMessage: "Failed to update Try-On integration",
      method: "PUT",
    })
  }

  async listTryOnPublishableKeys(merchantId?: string): Promise<{ items: TryOnPublishableKey[] }> {
    return this.requestAdminJson<{ items: TryOnPublishableKey[] }>(
      this.withMerchantQuery("/try-on/publishable-keys", merchantId),
      {
        defaultErrorMessage: "Failed to load keys",
        method: "GET",
      }
    )
  }

  async createTryOnPublishableKey(payload: {
    allowedOrigins: string[]
    label?: string
    merchantId?: string
  }): Promise<TryOnPublishableKeySecret> {
    return this.requestAdminJson<TryOnPublishableKeySecret>("/try-on/publishable-keys", {
      body: JSON.stringify(payload),
      contentType: "application/json",
      defaultErrorMessage: "Failed to create the integration",
      method: "POST",
    })
  }

  /** Rename an integration or change its Store URL — the key never changes. */
  async updateTryOnPublishableKey(
    keyId: string,
    payload: { allowedOrigins?: string[]; label?: string; merchantId?: string }
  ): Promise<TryOnPublishableKey> {
    return this.requestAdminJson<TryOnPublishableKey>(
      `/try-on/publishable-keys/${encodeURIComponent(keyId)}`,
      {
        body: JSON.stringify(payload),
        contentType: "application/json",
        defaultErrorMessage: "Failed to update the integration",
        method: "PUT",
      }
    )
  }

  async revokeTryOnPublishableKey(
    keyId: string,
    merchantId?: string
  ): Promise<TryOnPublishableKey> {
    return this.requestAdminJson<TryOnPublishableKey>(
      this.withMerchantQuery(`/try-on/publishable-keys/${encodeURIComponent(keyId)}`, merchantId),
      {
        defaultErrorMessage: "Failed to revoke key",
        method: "DELETE",
      }
    )
  }
}

export const adminApi = new AdminApi()
